function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  )
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

class CustomUtilities {
  static waitForMutationsEnd(targetNode, callback, options = { childList: true, subtree: true }, debounceTime = 100) {
    let timeoutId

    const observer = new MutationObserver(() => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }

      timeoutId = setTimeout(() => {
        observer.disconnect()
        callback()
      }, debounceTime)
    });

    observer.observe(targetNode, options)
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button")
  summary.setAttribute("aria-expanded", summary.parentNode.hasAttribute("open"))

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id)
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    )
  })

  if (summary.closest("header-drawer, menu-drawer")) return
  summary.parentElement.addEventListener("keyup", onKeyUpEscape)
})

document.addEventListener('DOMContentLoaded', () => {
  const panels = Array.from(document.querySelectorAll('details *[dynamic-position]'));

  const computeAndApply = (panel) => {
    // Reset before measure
    Object.assign(panel.style, {
      left: '',
      right: '',
      width: '',
      transform: 'none',
    });

    const vw = document.documentElement.clientWidth;
    const rect = panel.getBoundingClientRect();

    // Full-width fallback
    if (rect.width > vw) {
      panel.style.left = '0';
      panel.style.width = '100svw';
      requestAnimationFrame(() => {
        panel.setAttribute('data-positioned', 'true');
      })
      return;
    }

    // Anchor to trigger if overflowing right: "open left"
    const details = panel.closest('details');
    const summary = details?.querySelector(':scope > summary');

    let shift = 0;

    if (rect.right > vw && summary) {
      const t = summary.getBoundingClientRect();
      shift = (t.right - rect.right);
      const newLeft = rect.left + shift;
      const newRight = rect.right + shift;
      if (newLeft < 0) shift += -newLeft;
      else if (newRight > vw) shift += vw - newRight;
    } else {
      if (rect.left < 0) shift += -rect.left;
      if (rect.right > vw) shift += vw - rect.right;
    }

    if (shift) panel.style.transform = `translateX(${Math.round(shift)}px)`;

    // Force a reflow so the browser commits styles while still hidden/no-transition
    panel.offsetWidth;
    requestAnimationFrame(() => {
      panel.setAttribute('data-positioned', 'true');
    })
  };

  // Ensure we run after details paints open state:
  const afterOpen = (fn) =>
    requestAnimationFrame(() => requestAnimationFrame(() => requestAnimationFrame(fn)));
  // ^ triple RAF: paint open → layout settle → compute/apply → reveal next frame

  const openPanels = new Set();
  const onResize = () => {
    openPanels.forEach((panel) => {
      computeAndApply(panel);
    });
  };
  window.addEventListener('resize', onResize);

  panels.forEach((panel) => {
    panel.removeAttribute('data-positioned');

    const details = panel.closest('details');
    if (!details) return;

    details.addEventListener('toggle', () => {
      if (details.open) {
        afterOpen(() => {
          computeAndApply(panel);
          openPanels.add(panel);
        });
      } else {
        panel.removeAttribute('data-positioned');
        panel.style.transform = 'none';
        openPanels.delete(panel);
      }
    });

    if (details.open) {
      afterOpen(() => {
        computeAndApply(panel);
        openPanels.add(panel);
      });
    }
  });
});


document.addEventListener("DOMContentLoaded", () => {
  const detailsToCloseNodeList = document.querySelectorAll(`[data-close-on-element-click]`)
  const detailsToCloseArray = Array.from(detailsToCloseNodeList)
  const closeTriggers = document.querySelectorAll(`[data-close-trigger]`)

  closeTriggers.forEach((trigger) => {
    trigger.addEventListener("click", () => {
      const closeTriggerId = trigger.getAttribute("data-close-trigger")

      const detailsToClose = detailsToCloseArray.find((details) =>
        details.getAttribute("data-close-on-element-click") === closeTriggerId
      )

      if (detailsToClose) {
        detailsToClose.removeAttribute("open")
      }
    })
  })
})

const trapFocusHandlers = {}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container)
  var first = elements[0]
  var last = elements[elements.length - 1]

  removeTrapFocus()

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return

    document.addEventListener("keydown", trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener("focusout", trapFocusHandlers.focusout)
  document.addEventListener("focusin", trapFocusHandlers.focusin)

  elementToFocus.focus()

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length)
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible")
} catch (e) {
  focusVisiblePolyfill()
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ]
  let currentFocusedElement = null
  let mouseClick = null

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false
    }
  })

  window.addEventListener("mousedown", (event) => {
    mouseClick = true
  })

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused")

      if (mouseClick) return

      currentFocusedElement = document.activeElement
      currentFocusedElement.classList.add("focused")
    },
    true
  )
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    )
  })
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*")
  })
  document.querySelectorAll("video").forEach((video) => video.pause())
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause()
  })
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin)
  document.removeEventListener("focusout", trapFocusHandlers.focusout)
  document.removeEventListener("keydown", trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return

  const openDetailsElement = event.target.closest("details[open]")
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector("summary")
  openDetailsElement.removeAttribute("open")
  summaryElement.setAttribute("aria-expanded", false)
  summaryElement.focus()
}

class QuantityInput extends HTMLElement {
  constructor() {
    super()
    this.input = this.querySelector("input")
    this.changeEvent = new Event("change", { bubbles: true })
    this.input.addEventListener("change", this.onInputChange.bind(this))
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    )
  }

  quantityUpdateUnsubscriber = undefined

  connectedCallback() {
    this.validateQtyRules()
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    )
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber()
    }
  }

  onInputChange(event) {
    this.validateQtyRules()
  }

  onButtonClick(event) {
    event.preventDefault()
    const previousValue = this.input.value

    event.target.name === "plus" ? this.input.stepUp() : this.input.stepDown()
    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent)
  }

  validateQtyRules() {
    const value = parseInt(this.input.value)
    if (this.input.min) {
      const min = parseInt(this.input.min)
      const buttonMinus = this.querySelector(".quantity__button[name='minus']")
      buttonMinus.classList.toggle("disabled", value <= min)
    }
    if (this.input.max) {
      const max = parseInt(this.input.max)
      const buttonPlus = this.querySelector(".quantity__button[name='plus']")
      buttonPlus.classList.toggle("disabled", value >= max)
    }
  }
}

customElements.define("quantity-input", QuantityInput)

function debounce(fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

function throttle(fn, delay, options) {
  let blocked = false
  let lastArgs

  return function (...args) {
    if (!blocked) {
      blocked = true
      fn(...args)

      setTimeout(() => {
        if (options.trailing && lastArgs) {
          fn(...lastArgs)
          lastArgs = undefined
        }

        blocked = false
      }, delay)
    } else {
      lastArgs = args
    }
  }
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  }
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {}
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments)
  }
}

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i]
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i
      return i
    }
  }
}

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback)
}

Shopify.postLink = function (path, options) {
  options = options || {}
  var method = options["method"] || "post"
  var params = options["parameters"] || {}

  var form = document.createElement("form")
  form.setAttribute("method", method)
  form.setAttribute("action", path)

  for (var key in params) {
    var hiddenField = document.createElement("input")
    hiddenField.setAttribute("type", "hidden")
    hiddenField.setAttribute("name", key)
    hiddenField.setAttribute("value", params[key])
    form.appendChild(hiddenField)
  }
  document.body.appendChild(form)
  form.submit()
  document.body.removeChild(form)
}

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid)
  this.provinceEl = document.getElementById(province_domid)
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  )

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  )

  this.initCountry()
  this.initProvince()
}

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default")
    Shopify.setSelectorByValue(this.countryEl, value)
    this.countryHandler()
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default")
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value)
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex]
    var raw = opt.getAttribute("data-provinces")
    var provinces = JSON.parse(raw)

    this.clearOptions(this.provinceEl)
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none"
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option")
        opt.value = provinces[i][0]
        opt.innerHTML = provinces[i][1]
        this.provinceEl.appendChild(opt)
      }

      this.provinceContainer.style.display = ""
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild)
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option")
      opt.value = values[i]
      opt.innerHTML = values[i]
      selector.appendChild(opt)
    }
  },
}

class MenuDrawer extends HTMLElement {
  constructor() {
    super()

    this.mainDetailsToggle = this.querySelector("details")

    this.addEventListener("keyup", this.onKeyUp.bind(this))
    this.addEventListener("focusout", this.onFocusOut.bind(this))
    this.bindEvents()
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    )
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    )
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return

    const openDetailsElement = event.target.closest("details[open]")
    if (!openDetailsElement) return

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
        event,
        this.mainDetailsToggle.querySelector("summary")
      )
      : this.closeSubmenu(openDetailsElement)
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget
    const detailsElement = summaryElement.parentNode
    const parentMenuElement = detailsElement.closest(".has-submenu")
    const isOpen = detailsElement.hasAttribute("open")
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      )
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      )
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault()
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement)

      if (window.matchMedia("(max-width: 990px)")) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        )
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening")
        summaryElement.setAttribute("aria-expanded", true)
        parentMenuElement && parentMenuElement.classList.add("submenu-open")
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
            "transitionend",
            addTrapFocus
          )
      }, 100)
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening")
    })
    summaryElement.setAttribute("aria-expanded", true)
    trapFocus(this.mainDetailsToggle, summaryElement)
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`)
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return

    this.mainDetailsToggle.classList.remove("menu-opening")
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      if (details.getAttribute('open') === 'always') return

      details.removeAttribute("open")
      details.classList.remove("menu-opening")
    })
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open")
      })
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`)
    removeTrapFocus(elementToFocus)
    this.closeAnimation(this.mainDetailsToggle)

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false)
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer()
    })
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details")

    if (detailsElement.getAttribute('open') === 'always') return

    this.closeSubmenu(detailsElement)
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open")
    parentMenuElement && parentMenuElement.classList.remove("submenu-open")
    detailsElement.classList.remove("menu-opening")
    detailsElement.querySelector("summary").setAttribute("aria-expanded", false)
    removeTrapFocus(detailsElement.querySelector("summary"))
    this.closeAnimation(detailsElement)
  }

  closeAnimation(detailsElement) {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      const elapsedTime = time - animationStart

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation)
      } else {
        detailsElement.removeAttribute("open")
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          )
        }
      }
    }

    window.requestAnimationFrame(handleAnimation)
  }
}

customElements.define("menu-drawer", MenuDrawer)

class AudioButton extends HTMLElement {
  constructor() {
    super()

    this.audio = this.querySelector("audio")
    this.muteButton = this.querySelector('button[mute]')
    this.playButton = this.querySelector('button[play]')
  }

  connectedCallback() {
    this.muteButton.addEventListener('click', () => {
      this.audio.pause()
      this.toggleAttribute('muted')
    })

    this.playButton.addEventListener('click', () => {
      this.audio.play()
      this.toggleAttribute('muted')
    })
  }
}

customElements.define("audio-button", AudioButton)

class ToggleOptions extends HTMLElement {
  constructor() {
    super()
    this._internals = this.attachInternals()

    this.buttons = this.querySelectorAll('button[data-target]')
    this.content = this.querySelectorAll(".sizes__content")

    this.buttons.forEach((button) => {
      button.addEventListener("click", () => this.toggle(button?.dataset?.target))
    })
  }

  toggle(id) {
    this.content.forEach(node => {
      if (id === node.id) {
        node.setAttribute("show", "")
      } else {
        node.removeAttribute("show")
      }

      this.buttons.forEach(button => {
        if (button?.dataset?.target === id) {
          button.setAttribute('active', '')
        } else {
          button.removeAttribute('active')
        }
      })
    })
  }
}

customElements.define("toggle-options", ToggleOptions)

class ModalDialog extends HTMLElement {
  constructor() {
    super()
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    )
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide()
    })
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide()
      })
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide()
      })
    }
  }

  connectedCallback() {
    if (this.moved) return
    this.moved = true
    document.body.appendChild(this)
  }

  show(opener) {
    this.openedBy = opener
    const popup = this.querySelector(".template-popup")
    document.body.classList.add("overflow-hidden")
    this.setAttribute("open", "")
    if (popup) popup.loadContent()
    trapFocus(this, this.querySelector('[role="dialog"]'))
    window.pauseAllMedia()
  }

  hide() {
    document.body.classList.remove("overflow-hidden")
    document.body.dispatchEvent(new CustomEvent("modalClosed"))
    this.removeAttribute("open")
    removeTrapFocus(this.openedBy)
    window.pauseAllMedia()
  }
}
customElements.define("modal-dialog", ModalDialog)

class ModalOpener extends HTMLElement {
  constructor() {
    super()

    const button = this.querySelector("button")

    if (!button) return
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"))
      if (modal) modal.show(button)
    })
  }
}
customElements.define("modal-opener", ModalOpener)

class DrawerDialog extends ModalDialog {
  constructor() {
    super()
  }
}

customElements.define("drawer-dialog", DrawerDialog)

class DrawerOpener extends HTMLElement {
  constructor() {
    super()

    const button = this.querySelector("button")

    if (!button) return
    button.addEventListener("click", () => {
      const drawer = document.querySelector(this.getAttribute("data-drawer"))
      if (drawer) drawer.show(button)
    })
  }
}
customElements.define("drawer-opener", DrawerOpener)

class DeferredMedia extends HTMLElement {
  constructor() {
    super()
    const poster = this.querySelector('[id^="Deferred-Poster-"]')
    if (!poster) return
    poster.addEventListener("click", this.loadContent.bind(this))
  }

  loadContent(focus = true) {
    window.pauseAllMedia()
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div")
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      )

      this.setAttribute("loaded", true)
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      )
      if (focus) deferredElement.focus()
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play()
      }
    }
  }
}

customElements.define("deferred-media", DeferredMedia)

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = this.dataset.enableSliderLooping === 'true' || false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.orientation = this.getAttribute('orientation');
    this.allSliderDots = this.querySelectorAll('.slider-counter__slide-dot');

    if (!this.slider) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton?.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton?.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;

    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );


    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057

    if (!this.slider) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dataset.activeIndex = this.currentPage;

      this.allSliderDots?.forEach((dotNode) => {
        if (dotNode) {
          dotNode.classList.remove('slider-counter__slide-dot--active');

          if (dotNode.dataset.slideDotIndex === String(this.currentPage)) {
            dotNode.classList.add('slider-counter__slide-dot--active');
          }
        }
      });

      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) {
      return;
    }

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton?.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton?.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton?.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton?.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;

    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  isSlideFullyVisible(element, offset = 0) {
    const sliderStart = this.slider.scrollLeft + offset;
    const sliderEnd = this.slider.clientWidth + this.slider.scrollLeft - offset;

    const elementStart = element.offsetLeft;
    // -1 is a workaround for last slide isn't visible sometimes
    const elementEnd = element.offsetLeft + element.clientWidth - 1;

    // Fully visible if the element is completely within the bounds
    return elementStart >= sliderStart && elementEnd <= sliderEnd;
  }

  onButtonClick(event) {
    event.preventDefault();
    const stepDesktop = event.currentTarget.dataset.step || 1;
    const stepMobile = event.currentTarget.dataset.stepMobile || 1;

    const step = window.matchMedia('(min-width: 1200px)').matches ? stepDesktop : stepMobile;

    
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent)

class SlideshowComponent extends SliderComponent {
  constructor() {
    super()
    this.sliderControlWrapper = this.querySelector(".slider-buttons")
    this.enableSliderLooping = true

    if (!this.sliderControlWrapper) return

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide")
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider")
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    )
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    )
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this))
    this.setSlideVisibility()

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false

      this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)")
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay()
      })
        ;[this?.prevButton, this?.nextButton].forEach((button) => {
          button.addEventListener(
            "click",
            () => {
              this.announcementBarArrowButtonWasClicked = true
            },
            { once: true }
          )
        })
    }

    if (this.slider.getAttribute("data-autoplay") === "true") this.setAutoPlay()
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000
    this.addEventListener("mouseover", this.focusInHandling.bind(this))
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this))
    this.addEventListener("focusin", this.focusInHandling.bind(this))
    this.addEventListener("focusout", this.focusOutHandling.bind(this))

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay")
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      )
      this.autoplayButtonIsSetToPlay = true
      this.play()
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play()
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event)
    this.wasClicked = true

    const isFirstSlide = this.currentPage === 1
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name)
      return
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0
    }

    this.setSlidePosition(this.slideScrollPosition)

    this.applyAnimationToAnnouncementBar(event.currentTarget.name)
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout)
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      })
    }, this.announcerBarAnimationDelay)
  }

  update() {
    super.update()
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link")
    this.prevButton?.removeAttribute("disabled")

    if (!this.sliderControlButtons.length) return

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active")
      link.removeAttribute("aria-current")
    })
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    )
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    )
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay)
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play()
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target)
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return
      this.play()
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play()
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target)
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play()
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause()
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause()
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off")
    clearInterval(this.autoplay)
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    )
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite")
    clearInterval(this.autoplay)
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused")
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      )
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused")
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      )
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset

    this.setSlidePosition(slideScrollPosition)
    this.applyAnimationToAnnouncementBar()
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a")
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex")
          })
        item.setAttribute("aria-hidden", "false")
        item.removeAttribute("tabindex")
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1")
          })
        item.setAttribute("aria-hidden", "true")
        item.setAttribute("tabindex", "-1")
      }
    })
    this.wasClicked = false
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return

    const itemsCount = this.sliderItems.length
    const increment = button === "next" ? 1 : -1

    const currentIndex = this.currentPage - 1
    let nextIndex = (currentIndex + increment) % itemsCount
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex

    const nextSlide = this.sliderItems[nextIndex]
    const currentSlide = this.sliderItems[currentIndex]

    const animationClassIn = "announcement-bar-slider--fade-in"
    const animationClassOut = "announcement-bar-slider--fade-out"

    const isFirstSlide = currentIndex === 0
    const isLastSlide = currentIndex === itemsCount - 1

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide)
    const direction = shouldMoveNext ? "next" : "previous"

    currentSlide.classList.add(`${animationClassOut}-${direction}`)
    nextSlide.classList.add(`${animationClassIn}-${direction}`)

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`)
      nextSlide.classList.remove(`${animationClassIn}-${direction}`)
    }, this.announcerBarAnimationDelay * 2)
  }

  linkToSlide(event) {
    event.preventDefault()
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) +
        1 -
        this.currentPage)
    this.slider.scrollTo({
      left: slideScrollPosition,
    })
  }
}

customElements.define("slideshow-component", SlideshowComponent)

class VariantSelects extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    let t;
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);
      clearTimeout(t);
      t = setTimeout(() => {
          window.refreshOrdersify && window.refreshOrdersify();
      }, 1000);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define("variant-selects", VariantSelects)

class ProductRecommendations extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return
      observer.unobserve(this)

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div")
          html.innerHTML = text
          const recommendations = html.querySelector("product-recommendations")

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML
          }

          if (
            !this.querySelector("slideshow-component") &&
            this.classList.contains("complementary-products")
          ) {
            this.remove()
          }

          if (html.querySelector(".grid__item")) {
            this.classList.add("product-recommendations--loaded")
          }
        })
        .catch((e) => {
          console.error(e)
        })
    }

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 800px 0px",
    }).observe(this)
  }
}

customElements.define("product-recommendations", ProductRecommendations)

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      metricName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

class ProductTabs extends HTMLElement {
  constructor() {
    super()
  }

  connectedCallback() {
    this.tabs = this.querySelectorAll(".product__tabs-tab-wrapper")

    this.tabs.forEach((tab) => {
      const tabExpandableContent = tab.querySelector(".product__tabs-tab-inner")
      if (!tabExpandableContent) {
        return (tab.style.display = "none")
      }
      tabExpandableContent.ariaHidden = true

      const tabRow = tab.querySelector(".product__tabs-tab-row")

      tabRow.addEventListener("click", () => {
        if (tab.classList.contains("active")) {
          this.removeTabActiveState(tab)
        } else {
          this.tabs.forEach(this.removeTabActiveState)
          this.showTabDescription(tab)
        }
      });

      if (tab.classList.contains("active")) {
        this.assignMaxHeight(tabExpandableContent)
      }
    })
  }

  getTallestChildHeight(wrapper) {
    if (!wrapper || wrapper.children.length === 0) {
      return 0 // Return 0 if no wrapper or no children
    }

    // Initialize the tallest height found to 0
    let tallestHeight = 0

    for (const child of wrapper.children) {
      const childHeight = child.offsetHeight

      if (childHeight > tallestHeight) {
        tallestHeight = childHeight
      }
    }

    return tallestHeight
  }

  removeTabActiveState = (tab) => {
    tab.classList.remove("active")
    const tabExpandableContent = tab.querySelector(".product__tabs-tab-inner")

    if (tabExpandableContent) {
      tabExpandableContent.ariaHidden = true
      tabExpandableContent.classList.remove("product__tabs-tab-inner--expanded")
      tabExpandableContent.classList.add("product__tabs-tab-inner--collapsed")
      tabExpandableContent.style = `max-height: 0px;min-height: 0px;`
    }
  }

  assignStylesToChild = (child) => {
    if (child.tagName === "P") {
      child.style = "margin: 0;padding-left: 24px;"
    }

    child.style = "word-break: break-word;"
  }

  assignStylesToChildren = (children) =>
    Array.from(children).forEach((child) => {
      if (child.children.length) {
        this.assignStylesToChildren(child.children)
      } else {
        this.assignStylesToChild(child)
      }
    })

  assignMaxHeight = (tabExpandableContent) => {
    tabExpandableContent.ariaHidden = false
    this.assignStylesToChildren(tabExpandableContent.children)

    const CONTENT_PADDING_PX = 32

    const tabDescriptionHeight =
      this.getTallestChildHeight(tabExpandableContent) + CONTENT_PADDING_PX

    tabExpandableContent.classList.add("product__tabs-tab-inner--expanded")
    tabExpandableContent.classList.remove("product__tabs-tab-inner--collapsed")
    tabExpandableContent.classList.remove("initial--expanded")

    tabExpandableContent.style = `min-height: ${tabDescriptionHeight}px;max-height: ${tabDescriptionHeight}px;`
  };

  showTabDescription = (tab) => {
    tab.classList.add("active")

    const tabExpandableContent = tab.querySelector(".product__tabs-tab-inner")
    if (!tabExpandableContent) return

    tabExpandableContent.ariaHidden = false
    this.assignStylesToChildren(tabExpandableContent.children)

    this.assignMaxHeight(tabExpandableContent)
  };

  disconnectedCallback() { }
}

customElements.define("product-tabs", ProductTabs)